import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApplicationMetrics,
  ApplicationMetricsInclude,
  ApplicationsContract,
} from '@terra/shared-contracts-applications';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { LIST_BADGE_LIFECYCLE_STATE } from '@terra/shared-dto-types';
import { DETECTION_VIEW } from '@terra/ui-design-system';
import { useTargetType } from '../../store/projectStore';
import { countFindingsForTarget } from '../api/target-findings-api';
import { terraApiWrapper } from '../../utils/terraApiWrapper';
import { neoExecutionsCountsQueryOptions } from './neo-executions-count';

const KPI_METRICS_INCLUDE: ApplicationMetricsInclude[] = [
  'findings',
  'signals',
  'assets',
];

type KpiMetrics = {
  findings: { total: number };
  signals: { total: number };
  assets: { total: number };
  executions: { total: number };
};

const toKpiMetrics = ({
  metrics,
  executionsTotal,
}: {
  metrics: ApplicationMetrics | undefined;
  executionsTotal: number;
}): Omit<KpiMetrics, 'findings'> => ({
  signals: { total: metrics?.signals?.total ?? 0 },
  assets: { total: metrics?.assets?.total ?? 0 },
  executions: { total: executionsTotal },
});

export const fetchTorchApplicationMetrics = async ({
  applicationId,
  from,
  to,
}: {
  applicationId: string;
  from?: Date;
  to?: Date;
}): Promise<ApplicationMetrics | undefined> => {
  const response = await terraApiWrapper<
    typeof ApplicationsContract.getApplicationsMetrics
  >({
    endpointFn: () =>
      applicationsClient.getApplicationsMetrics({
        query: {
          applicationIds: [applicationId],
          detectionView: DETECTION_VIEW.ALL,
          include: KPI_METRICS_INCLUDE,
          ...(from && { from: from.toISOString() }),
          ...(to && { to: to.toISOString() }),
        },
      }),
  });

  return response?.[applicationId];
};

export function useKpiMetrics(applicationId: string) {
  const targetType = useTargetType();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [
      'kpi-metrics',
      applicationId,
      targetType,
      DETECTION_VIEW.ALL,
      KPI_METRICS_INCLUDE,
    ],
    queryFn: async (): Promise<KpiMetrics> => {
      const [metrics, findingsTotal, executions] = await Promise.all([
        fetchTorchApplicationMetrics({ applicationId }),
        countFindingsForTarget({
          targetId: applicationId,
          targetType,
          filter: { lifecycleState: LIST_BADGE_LIFECYCLE_STATE },
        }),
        queryClient.fetchQuery(neoExecutionsCountsQueryOptions(applicationId)),
      ]);

      return {
        ...toKpiMetrics({ metrics, executionsTotal: executions.total }),
        findings: { total: findingsTotal },
      };
    },
    enabled: !!applicationId,
    staleTime: 30000,
  });
}
